export const APPLE_HEALTH_FALLBACK_DEVICE_ID = 'apple-health-ios';

const SUPPORTED_SAMPLE_TYPES = new Set([
  'active_calories',
  'body_weight',
  'distance',
  'heart_rate',
  'resting_heart_rate',
  'sleep',
  'sleep_duration',
  'steps',
  'total_calories',
  'workout_minutes',
]);

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const compactObject = (value) =>
  Object.fromEntries(
    Object.entries(value || {}).filter(([, item]) => item !== null && item !== undefined && item !== '')
  );

const stableSourceRecordId = (parts) =>
  parts
    .map((part) => String(part ?? '').trim())
    .filter(Boolean)
    .join(':')
    .slice(0, 240);

const normalizeType = (value) => {
  const normalized = String(value || '')
    .trim()
    .replace(/^HKQuantityTypeIdentifier/i, '')
    .replace(/^HKCategoryTypeIdentifier/i, '')
    .replace(/^HKWorkoutTypeIdentifier/i, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase();
  const aliases = {
    active_energy: 'active_calories',
    activeenergyburned: 'active_calories',
    active_energy_burned: 'active_calories',
    apple_exercise_time: 'workout_minutes',
    appleexercisetime: 'workout_minutes',
    apple_stand_time: 'workout_minutes',
    body_mass: 'body_weight',
    bodymass: 'body_weight',
    body_weight: 'body_weight',
    distance_walking_running: 'distance',
    distancewalkingrunning: 'distance',
    flights_climbed: 'steps',
    heart_rate: 'heart_rate',
    heartrate: 'heart_rate',
    resting_heart_rate: 'resting_heart_rate',
    restingheartrate: 'resting_heart_rate',
    sleep_analysis: 'sleep_duration',
    sleepanalysis: 'sleep_duration',
    sleep: 'sleep_duration',
    step_count: 'steps',
    stepcount: 'steps',
    total_energy: 'total_calories',
    totalenergyburned: 'total_calories',
    total_energy_burned: 'total_calories',
  };
  return aliases[normalized] || normalized;
};

const unitForSample = (sample) => {
  const explicit = String(sample?.unit || '').trim();
  if (explicit) return explicit;
  const metricType = normalizeType(sample?.metric_type || sample?.type);
  if (metricType.includes('calories')) return 'kcal';
  if (metricType === 'distance') return 'm';
  if (metricType === 'heart_rate' || metricType === 'resting_heart_rate') return 'bpm';
  if (metricType === 'body_weight') return 'kg';
  if (metricType === 'sleep_duration' || metricType === 'workout_minutes') return 'min';
  if (metricType === 'steps') return 'count';
  return 'other';
};

const recordedAtForSample = (sample) =>
  sample?.end_at || sample?.endDate || sample?.recorded_at || sample?.time || sample?.start_at || sample?.startDate || null;

const safeMetricMetadata = (sample) =>
  compactObject({
    aggregation: sample?.aggregation,
    native_metric_type: sample?.metric_type || sample?.type,
    timezone: sample?.timezone,
    device_name: sample?.device_name || sample?.deviceName,
    apple_health_quantity_type: sample?.quantity_type || sample?.quantityType,
  });

const appleHealthSampleToMetric = (sample, deviceId) => {
  const metricType = normalizeType(sample?.metric_type || sample?.type);
  if (!SUPPORTED_SAMPLE_TYPES.has(metricType)) return null;

  const value = toFiniteNumber(sample?.value);
  const recordedAt = recordedAtForSample(sample);
  const unit = unitForSample(sample);
  if (value === null || !recordedAt || !unit) return null;

  const sourceRecordId =
    sample?.provider_sample_id ||
    sample?.source_record_id ||
    sample?.uuid ||
    stableSourceRecordId([
      'apple_health',
      metricType,
      sample?.start_at || sample?.startDate,
      sample?.end_at || sample?.endDate,
      recordedAt,
      value,
      unit,
    ]);

  return compactObject({
    source_record_id: sourceRecordId,
    device_id: sample?.device_id || sample?.deviceId || deviceId,
    recorded_at: recordedAt,
    metric_type: metricType,
    value,
    unit,
    metadata: safeMetricMetadata(sample),
  });
};

const equipmentTypeForWorkout = (workoutType) => {
  const normalized = String(workoutType || '').trim().toLowerCase();
  if (normalized.includes('cycling') || normalized.includes('bike')) return 'stationary_bike';
  if (normalized.includes('elliptical')) return 'elliptical';
  if (normalized.includes('rowing')) return 'rowing_machine';
  if (normalized.includes('running') || normalized.includes('walking')) return 'treadmill';
  return 'other';
};

const durationSecondsForWorkout = (workout) => {
  const durationSec = toFiniteNumber(workout?.duration_sec ?? workout?.durationSeconds);
  if (durationSec !== null) return Math.max(0, Math.round(durationSec));
  const durationMin = toFiniteNumber(workout?.duration_min ?? workout?.durationMinutes);
  if (durationMin !== null) return Math.max(0, Math.round(durationMin * 60));
  const start = Date.parse(workout?.start_at ?? workout?.startDate);
  const end = Date.parse(workout?.end_at ?? workout?.endDate);
  if (Number.isFinite(start) && Number.isFinite(end) && end >= start) {
    return Math.round((end - start) / 1000);
  }
  return null;
};

const safeWorkoutMetadata = (workout) =>
  compactObject({
    native_workout_type: workout?.workout_type || workout?.activityType,
    timezone: workout?.timezone,
    device_name: workout?.device_name || workout?.deviceName,
    apple_health_workout_type: workout?.apple_health_workout_type || workout?.activityType,
  });

const appleHealthWorkoutToImportWorkout = (workout, deviceId) => {
  const startedAt = workout?.start_at ?? workout?.startDate;
  const endedAt = workout?.end_at ?? workout?.endDate;
  if (!startedAt || !endedAt) return null;

  const durationSec = durationSecondsForWorkout(workout);
  const calories =
    toFiniteNumber(workout?.active_calories_kcal ?? workout?.activeEnergyBurnedKcal) ??
    toFiniteNumber(workout?.total_calories_kcal ?? workout?.totalEnergyBurnedKcal);
  const distanceM = toFiniteNumber(workout?.distance_m ?? workout?.distanceMeters);
  const heartRateAvg = toFiniteNumber(workout?.avg_heart_rate_bpm ?? workout?.averageHeartRateBpm);
  const heartRateMax = toFiniteNumber(workout?.max_heart_rate_bpm ?? workout?.maxHeartRateBpm);
  const workoutType = String(workout?.workout_type || workout?.activityType || '').trim().toLowerCase();
  const sourceRecordId =
    workout?.provider_workout_id ||
    workout?.source_record_id ||
    workout?.uuid ||
    stableSourceRecordId([
      'apple_health',
      'workout',
      workoutType,
      startedAt,
      endedAt,
      durationSec,
      calories,
      distanceM,
    ]);

  return compactObject({
    source_record_id: sourceRecordId,
    device_id: workout?.device_id || workout?.deviceId || deviceId,
    equipment_type: equipmentTypeForWorkout(workoutType),
    started_at: startedAt,
    ended_at: endedAt,
    completed: true,
    duration_sec: durationSec,
    distance_m: distanceM,
    calories,
    heart_rate_avg: heartRateAvg !== null ? Math.round(heartRateAvg) : null,
    heart_rate_max: heartRateMax !== null ? Math.round(heartRateMax) : null,
    metadata: safeWorkoutMetadata(workout),
  });
};

export const buildAppleHealthImportPayload = (nativePayload, options = {}) => {
  const deviceId = options.device_id || options.deviceId || APPLE_HEALTH_FALLBACK_DEVICE_ID;
  const appInstanceId = options.app_instance_id || options.appInstanceId || deviceId;
  const samples = Array.isArray(nativePayload?.samples) ? nativePayload.samples : [];
  const workouts = Array.isArray(nativePayload?.workouts) ? nativePayload.workouts : [];

  return {
    source: 'apple_health',
    provider: 'apple_health',
    device_id: deviceId,
    platform: 'ios',
    app_instance_id: appInstanceId,
    background_sync_enabled: Boolean(options.background_sync_enabled || options.backgroundSyncEnabled),
    metrics: samples
      .map((sample) => appleHealthSampleToMetric(sample, deviceId))
      .filter(Boolean),
    workouts: workouts
      .map((workout) => appleHealthWorkoutToImportWorkout(workout, deviceId))
      .filter(Boolean),
  };
};

export const appleHealthLastProviderTimestamp = (nativePayload) => {
  const records = [
    ...(Array.isArray(nativePayload?.workouts) ? nativePayload.workouts : []),
    ...(Array.isArray(nativePayload?.samples) ? nativePayload.samples : []),
  ];
  const timestamps = records
    .flatMap((record) => [record?.end_at, record?.endDate, record?.recorded_at, record?.time, record?.start_at, record?.startDate])
    .map((value) => Date.parse(value))
    .filter((value) => Number.isFinite(value));
  if (!timestamps.length) return null;
  return new Date(Math.max(...timestamps)).toISOString();
};

export const shouldShowAppleHealthNativeAction = ({ nativeApp, platform } = {}) =>
  Boolean(nativeApp && platform === 'ios');

export const appleHealthDeviceId = (connection = null) =>
  connection?.id || connection?.device_id || APPLE_HEALTH_FALLBACK_DEVICE_ID;

export const appleHealthAppInstanceId = (connection = null) =>
  connection?.app_instance_id || appleHealthDeviceId(connection);

export const buildAppleHealthSourceStatusReport = (payload = {}) => ({
  status: payload.status || 'disabled',
  background_sync_enabled: false,
  error_code: payload.error_code || null,
  ...payload,
  background_sync_enabled: false,
});
